import { ApiError } from "api/client";
import { unsubscribeFromNewsletter } from "api/newsletter";
import { useSeo } from "hooks/useSeo";
import React, { type ChangeEvent, type FormEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Paths from "routes/Paths";

import styles from "./NewsletterUnsubscribePage.module.css";

type UnsubscribeStatus = "idle" | "submitting" | "done" | "error";

/**
 * Newsletter unsubscribe (GDPR Arts. 7(3) and 21(2), TTAPI-49). Works without an account. The API
 * answers the same way whether or not the address was subscribed, so the page always confirms the
 * same way too and reveals nothing about who is on the list.
 */
const NewsletterUnsubscribePage = () => {
	const { t } = useTranslation("common");
	const [email, setEmail] = useState("");
	const [status, setStatus] = useState<UnsubscribeStatus>("idle");
	const [errorMessage, setErrorMessage] = useState("");

	useSeo({ title: t("newsletterUnsubscribe.title"), noIndex: true, canonicalPath: Paths.newsletterUnsubscribe });

	const onEmailChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => setEmail(event.target.value), []);

	const onSubmit = useCallback(
		async (event: FormEvent<HTMLFormElement>): Promise<void> => {
			event.preventDefault();
			if (status === "submitting" || !email.trim()) {
				return;
			}

			setStatus("submitting");
			setErrorMessage("");
			try {
				await unsubscribeFromNewsletter(email.trim());
				setStatus("done");
			} catch (error) {
				setErrorMessage(error instanceof ApiError ? error.message : t("genericError"));
				setStatus("error");
			}
		},
		[email, status, t]
	);

	return (
		<main className={styles.root}>
			<section className={styles.panel}>
				<h1>{t("newsletterUnsubscribe.title")}</h1>
				{status === "done" ? (
					<p className={styles.confirmation} role="status">
						{t("newsletterUnsubscribe.done")}
					</p>
				) : (
					<>
						<p>{t("newsletterUnsubscribe.description")}</p>
						<form className={styles.form} onSubmit={onSubmit}>
							<label className={styles.field} htmlFor="newsletter-unsubscribe-email">
								<span>{t("newsletterUnsubscribe.emailLabel")}</span>
								<input
									autoComplete="email"
									disabled={status === "submitting"}
									id="newsletter-unsubscribe-email"
									onChange={onEmailChange}
									type="email"
									value={email}
									required
								/>
							</label>
							<button disabled={status === "submitting"} type="submit">
								{status === "submitting"
									? t("newsletterUnsubscribe.submitting")
									: t("newsletterUnsubscribe.submit")}
							</button>
						</form>
						{status === "error" && (
							<p className={styles.error} role="alert">
								{errorMessage}
							</p>
						)}
					</>
				)}
				<Link to={Paths.root}>{t("newsletterUnsubscribe.backHome")}</Link>
			</section>
		</main>
	);
};

export default NewsletterUnsubscribePage;
